import type { JSX } from 'react';
import type { HealthIssue, HealthSample, PeerHealth } from '../../health/api';
import type { PeerStats } from '../../net/api';
import { downloadBlob, timestampForFile } from '../actions/files';
import { Icon } from '../components/Icon';
import { useRuntime, useStudio } from '../context';
import { formatKbps, orDash } from '../format';
import { useTicker } from '../hooks/useTicker';
import { safely } from '../safe';
import {
  EVENT_LABELS,
  formatAge,
  formatClock,
  formatEncoder,
  formatFlag,
  formatFps,
  formatPercent,
  formatSkew,
  RECOVERY_LABELS,
  sortIssues,
  summarizeHealth,
} from './healthModel';
import './health.css';

function Stat({ label, value, alert }: { label: string; value: string; alert?: boolean }): JSX.Element {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={alert ? 'ui-error-text' : undefined}>{value}</dd>
    </div>
  );
}

function IssueRow({ issue, now, peerName }: { issue: HealthIssue; now: number; peerName: string | null }): JSX.Element {
  return (
    <li className={`health-issue ${issue.severity}`} data-issue={issue.code}>
      <span className={`health-dot ${issue.severity}`} aria-hidden="true" />
      <div className="ui-grow">
        <div className="health-issue-message ui-selectable">{issue.message}</div>
        <div className="ui-dim">
          <span className="health-severity">{issue.severity}</span> · for {formatAge(now - issue.since)}
          {peerName ? ` · ${peerName}` : ''} · <span className={issue.recovery === 'failed' ? 'ui-error-text' : undefined}>{RECOVERY_LABELS[issue.recovery]}</span>
        </div>
      </div>
    </li>
  );
}

function PipelineReadout({ sample }: { sample: HealthSample }): JSX.Element {
  const { video, audio, visibility } = sample;
  const audioDown = audio.contextState !== 'running';
  return (
    <dl className="stats-grid">
      <Stat label="Produced" value={`${formatFps(video.producedFps)} / ${video.targetFps} fps`} alert={video.producedFps < video.targetFps * 0.5} />
      <Stat label="Delivered" value={`${formatFps(video.deliveredFps)} fps`} alert={video.deliveredFps < video.targetFps * 0.5} />
      <Stat label="Last frame" value={`${formatAge(video.lastFrameAgeMs)} ago`} alert={video.lastFrameAgeMs > 1500} />
      <Stat label="Skipped frames" value={String(video.skippedFrames)} />
      <Stat label="Starved" value={`${formatFps(video.starvedFps)} /s`} alert={video.starvedFps > video.targetFps * 0.2} />
      <Stat label="Failed" value={`${formatFps(video.failedFps)} /s`} alert={video.failedFps > 0} />
      <Stat label="Dropped at output" value={String(video.droppedOutputFrames)} />
      <Stat label="GPU context" value={video.contextLost ? 'lost' : 'ok'} alert={video.contextLost} />
      <Stat label="Video track" value={video.trackMuted ? `${video.trackState}, muted` : video.trackState} alert={video.trackState !== 'live'} />
      <Stat label="Audio engine" value={audio.contextState} alert={audioDown} />
      <Stat label="Audio clock" value={`${audio.clockRate.toFixed(2)}×`} alert={audio.clockRate < 0.5} />
      <Stat label="Audio quanta" value={`${Math.round(audio.quantaPerSecond)} /s`} alert={audio.quantaPerSecond === 0} />
      <Stat label="Audio track" value={audio.trackState} alert={audio.trackState !== 'live'} />
      <Stat label="A/V skew" value={formatSkew(sample.avSkewMs)} />
      <Stat
        label="Page"
        value={visibility.state === 'hidden' ? `hidden for ${formatAge(visibility.hiddenForMs)}` : visibility.wasFrozen ? 'visible (was frozen)' : 'visible'}
      />
      {sample.recording ? <Stat label="Recording" value={sample.recording.active ? `${formatKbps((sample.recording.bytesPerSecond * 8) / 1000)}` : 'inactive'} /> : null}
    </dl>
  );
}

function PeerTable({ peers, stats, names }: { peers: PeerHealth[]; stats: Record<string, PeerStats>; names: Record<string, string> }): JSX.Element {
  return (
    <div className="health-scroll">
      <table className="ui-table">
        <thead>
          <tr>
            <th scope="col">Peer</th>
            <th scope="col">State</th>
            <th scope="col">Sent</th>
            <th scope="col">Received</th>
            <th scope="col">Frame age</th>
            <th scope="col">Report age</th>
            <th scope="col">Freezes</th>
            <th scope="col">Audio</th>
            <th scope="col">A/V sync</th>
            <th scope="col">RTT</th>
            <th scope="col">Loss</th>
            <th scope="col">Out</th>
            <th scope="col">Route</th>
            <th scope="col">Encoder</th>
            <th scope="col">Limited by</th>
          </tr>
        </thead>
        <tbody>
          {peers.map((peer) => {
            const net = stats[peer.peerId];
            return (
              <tr key={peer.peerId} data-peer={peer.peerId}>
                <th scope="row">{peer.peerId === 'whip' ? 'WHIP' : (names[peer.peerId] ?? peer.peerId.slice(0, 8))}</th>
                <td className={peer.connectionState === 'connected' ? undefined : 'ui-warn-text'}>{peer.connectionState}</td>
                <td className="ui-mono">{formatFps(peer.sentFps)}</td>
                <td className="ui-mono">{formatFps(peer.receivedFps)}</td>
                <td className="ui-mono">{formatAge(peer.receiverFrameAgeMs)}</td>
                <td className="ui-mono">{formatAge(peer.reportAgeMs)}</td>
                <td className="ui-mono">{orDash(peer.freezeCount)}</td>
                <td className={peer.audioFlowing === false ? 'ui-warn-text' : undefined}>{formatFlag(peer.audioFlowing, 'flowing', 'silent')}</td>
                <td className="ui-mono">{formatSkew(peer.receiverAvSyncMs)}</td>
                <td className="ui-mono">{peer.rttMs === null ? '—' : `${Math.round(peer.rttMs)} ms`}</td>
                <td className="ui-mono">{formatPercent(peer.packetLossPct)}</td>
                <td className="ui-mono">{formatKbps(peer.outKbps)}</td>
                <td>{orDash(peer.candidateType)}</td>
                <td>{formatEncoder(net?.encoderImplementation, net?.powerEfficientEncoder)}</td>
                <td>{orDash(peer.qualityLimitationReason)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Stream Health dock: what the watchdog sees, what it found and what it did about it. */
export function HealthPanel(): JSX.Element {
  const studio = useStudio();
  // The watchdog publishes about once a second; that is the only re-render cadence here.
  const health = useRuntime((r) => r.health);
  const network = useRuntime((r) => r.network);
  const peers = useRuntime((r) => r.broadcast.peers);
  const summary = summarizeHealth(health);
  const issues = sortIssues(health.issues);
  const now = useTicker(issues.length > 0, 1000);
  const names: Record<string, string> = {};
  for (const peer of peers) names[peer.peerId] = peer.name || peer.role;
  const sample = health.latest;
  const peerRows = sample ? [...sample.peers, ...(sample.whip ? [sample.whip] : [])] : [];
  const events = [...health.events].reverse();

  const exportDiagnostics = (): void =>
    void safely(
      studio,
      async () => {
        const blob = await studio.exportDiagnostics();
        // downloadBlob revokes its object URL once the browser has taken the file.
        downloadBlob(blob, `Diagnostics ${timestampForFile(new Date())}.json`);
      },
      'Could not export diagnostics',
    );

  return (
    <div className="ui-panel">
      <div className="ui-panel-body stats health" data-health={summary.tone}>
        <div className="ui-row">
          <span className={`health-dot ${summary.tone}`} aria-hidden="true" />
          <strong className="ui-grow" role="status" aria-label={`Stream health: ${summary.label}`}>
            {summary.label}
            {summary.tone === 'idle' ? <span className="ui-dim"> — start streaming or recording to monitor the pipeline</span> : null}
          </strong>
          <button type="button" className="ui-btn" onClick={exportDiagnostics}>
            <Icon name="copy" size={14} /> Export diagnostics
          </button>
        </div>

        <section aria-label="Current issues">
          <div className="ui-section-title">Current issues</div>
          {issues.length === 0 ? (
            <div className="ui-dim">{summary.tone === 'idle' ? 'Nothing is live.' : 'No issues.'}</div>
          ) : (
            <ul className="health-issues">
              {issues.map((issue) => (
                <IssueRow key={issue.id} issue={issue} now={now} peerName={issue.peerId ? (names[issue.peerId] ?? issue.peerId.slice(0, 8)) : null} />
              ))}
            </ul>
          )}
        </section>

        <section aria-label="Pipeline">
          <div className="ui-section-title">Pipeline</div>
          {sample ? <PipelineReadout sample={sample} /> : <div className="ui-dim">No measurements yet.</div>}
        </section>

        <section aria-label="Peers">
          <div className="ui-section-title">Peers</div>
          {peerRows.length === 0 ? <div className="ui-dim">No active connections.</div> : <PeerTable peers={peerRows} stats={network} names={names} />}
        </section>

        <section aria-label="Recent events">
          <div className="ui-section-title">Recent events</div>
          {events.length === 0 ? (
            <div className="ui-dim">Nothing has happened yet.</div>
          ) : (
            <ol className="health-events">
              {events.map((event, index) => (
                <li key={`${event.at}:${event.type}:${event.code}:${index}`} className={event.type === 'recovery-failed' ? 'ui-error-text' : undefined}>
                  <span className="ui-mono ui-dim">{formatClock(event.at)}</span> <span className="health-event-type">{EVENT_LABELS[event.type]}</span>{' '}
                  <span className="ui-selectable">{event.detail}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
